import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  makeStyles
} from '@material-ui/core'
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab'
import { TagReviewBatchStatus } from './types'

import { Search } from '@material-ui/icons'
import React from 'react'
import { HISTORY } from '../router'
import { useListTagReviewBatches } from './hooks/useListTagReviewBatches'

type StatusFilter = TagReviewBatchStatus | 'all'

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column' as const,
    minHeight: '100%'
  },
  header: {
    position: 'sticky' as const,
    top: 0,
    zIndex: 10,
    backgroundColor: theme.palette.background.default,
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2, 3)
  },
  headerContent: {
    maxWidth: 1100,
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto'
  },
  content: {
    maxWidth: 1100,
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(4),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(2)
  },
  controlsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap' as const
  },
  searchField: {
    flexGrow: 1,
    minWidth: 280
  },
  tableRow: {
    cursor: 'pointer'
  },
  titleCell: {
    fontWeight: 500
  },
  descriptionCell: {
    color: theme.palette.text.secondary,
    maxWidth: 360,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const
  },
  statusButton: {
    fontWeight: 600,
    textTransform: 'none' as const,
    minWidth: 0,
    padding: theme.spacing(0.25, 1.5),
    borderRadius: 16
  },
  emptyRow: {
    padding: theme.spacing(8, 3),
    textAlign: 'center' as const,
    color: theme.palette.text.secondary
  }
}))

const formatRelativeTime = (timestampMs: number): string => {
  const now = Date.now()
  const diffMs = now - timestampMs
  const diffMinutes = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)
  const diffWeeks = Math.floor(diffDays / 7)
  const diffMonths = Math.floor(diffDays / 30)

  if (diffMs < 0) return 'just now'
  if (diffMinutes < 1) return 'just now'
  if (diffMinutes < 60) return `${diffMinutes}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  if (diffWeeks < 5) return `${diffWeeks}w ago`
  if (diffMonths < 12) return `${diffMonths}mo ago`
  return `${Math.floor(diffMonths / 12)}y ago`
}

const formatAbsoluteTime = (timestampMs: number): string => {
  const date = new Date(timestampMs)
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const statusChipColor = (status: TagReviewBatchStatus): { bg: string; fg: string } => {
  switch (status) {
    case 'needs-review':
      return { bg: '#FFF3E0', fg: '#E65100' }
    case 'completed':
      return { bg: '#E8F5E9', fg: '#1B5E20' }
  }
}

const statusLabel = (status: TagReviewBatchStatus): string => {
  switch (status) {
    case 'needs-review':
      return 'Needs Review'
    case 'completed':
      return 'Completed'
  }
}

export const BatchList: React.FC = () => {
  const classes = useStyles()
  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(25)
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>('all')
  const [searchTerm, setSearchTerm] = React.useState('')
  const [createdSort, setCreatedSort] = React.useState<'asc' | 'desc'>('desc')

  const { data, loading } = useListTagReviewBatches()
  const batches = React.useMemo(() => data?.listTagReviewBatches?.batches ?? [], [data])

  const toggleCreatedSort = () => {
    setCreatedSort(prev => (prev === 'desc' ? 'asc' : 'desc'))
  }

  const statusCounts = React.useMemo(() => {
    const counts: Record<string, number> = { all: batches.length }
    for (const batch of batches) {
      counts[batch.status] = (counts[batch.status] || 0) + 1
    }
    return counts
  }, [batches])

  const filteredBatches = React.useMemo(() => {
    let result = [...batches]

    if (statusFilter !== 'all') {
      result = result.filter(b => b.status === statusFilter)
    }

    const term = searchTerm.trim().toLowerCase()
    if (term) {
      result = result.filter(b => b.title.toLowerCase().includes(term) || b.id.toLowerCase().includes(term))
    }

    result.sort((a, b) => (createdSort === 'desc' ? b.createdAt - a.createdAt : a.createdAt - b.createdAt))

    return result
  }, [batches, statusFilter, searchTerm, createdSort])

  const paginatedBatches = filteredBatches.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  const handleFilterChange = (_: React.MouseEvent<HTMLElement>, newValue: StatusFilter | null) => {
    if (newValue !== null) {
      setStatusFilter(newValue)
      setPage(0)
    }
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
    setPage(0)
  }

  const navigateToBatch = (id: string) => {
    HISTORY.push(`/tag-review/${id}`)
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.headerContent}>
          <Typography variant='h5'>Tag Review</Typography>
          <Typography variant='body2' color='textSecondary' style={{ marginTop: 4 }}>
            Curate and approve tags for Vestaboard content. Batches may include AI suggestions or require fully manual
            review.
          </Typography>
        </Box>
      </Box>
      <Box className={classes.content}>
        <Box className={classes.controlsRow}>
          <TextField
            className={classes.searchField}
            placeholder='Search by title, description, or ID'
            value={searchTerm}
            onChange={handleSearchChange}
            variant='outlined'
            size='small'
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <Search fontSize='small' />
                </InputAdornment>
              )
            }}
          />
          <ToggleButtonGroup value={statusFilter} exclusive onChange={handleFilterChange} size='small'>
            <ToggleButton value='all'>All ({statusCounts.all})</ToggleButton>
            <ToggleButton value='needs-review'>Needs Review ({statusCounts['needs-review'] || 0})</ToggleButton>
            <ToggleButton value='completed'>Completed ({statusCounts.completed || 0})</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <TableContainer component={Paper} variant='outlined'>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Description</TableCell>
                <TableCell sortDirection={createdSort}>
                  <TableSortLabel active direction={createdSort} onClick={toggleCreatedSort}>
                    Created
                  </TableSortLabel>
                </TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className={classes.emptyRow}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : paginatedBatches.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className={classes.emptyRow}>
                    No batches match the current filters.
                  </TableCell>
                </TableRow>
              ) : (
                paginatedBatches.map(batch => {
                  const colors = statusChipColor(batch.status as TagReviewBatchStatus)
                  return (
                    <TableRow
                      key={batch.id}
                      className={classes.tableRow}
                      onClick={() => navigateToBatch(batch.id)}
                      hover>
                      <TableCell className={classes.titleCell}>{batch.title}</TableCell>
                      <TableCell className={classes.descriptionCell}>
                        <Tooltip title={batch.description ?? ''} arrow>
                          <span>{batch.description ?? '—'}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={formatAbsoluteTime(batch.createdAt)} arrow>
                          <span>{formatRelativeTime(batch.createdAt)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {batch.counts.completed + batch.counts.rejected} / {batch.counts.total} reviewed
                      </TableCell>
                      <TableCell>
                        <Button
                          variant='contained'
                          disableElevation
                          size='small'
                          className={classes.statusButton}
                          style={{ backgroundColor: colors.bg, color: colors.fg }}
                          onClick={e => {
                            e.stopPropagation()
                            navigateToBatch(batch.id)
                          }}>
                          {statusLabel(batch.status as TagReviewBatchStatus)}
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component='div'
          count={filteredBatches.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => {
            setRowsPerPage(parseInt(e.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Box>
    </Box>
  )
}
